import {
  MAX_VISIBILITY_ALLOWLIST_SIZE,
  evaluateOrderComponentVisibility,
  isBooleanOrderComponent,
  parseVisibilityConditions,
  serializeVisibilityConditions,
} from '@moko/utils';
import type { VisibilityCondition, VisibilityScalar } from '@moko/utils';
import type { IField } from '../../services/metadataService';

export type VisibilityMode = 'always' | 'Show' | 'Hide';
export type VisibilityOperator = VisibilityCondition['operator'];

export interface VisibilityRowDraft {
  controller: string;
  operator: VisibilityOperator;
  text: string;
}

export interface VisibilityDraft {
  mode: VisibilityMode;
  rows: VisibilityRowDraft[];
  editable: boolean;
}

export interface VisibilityRule {
  dependees?: string;
  effect?: IField['effect'];
}

export type VisibilityRuleResult = { rule: VisibilityRule } | { error: string };

const CONTROLLER_FIELD_TYPES = new Set([
  'String',
  'Number',
  'Checkbox',
  'RadioButton',
  'AutoComplete',
  'OrderComponent',
]);

const LINE_BREAK = /\r?\n/;

export const controllerCandidates = (fields: IField[], self: IField) =>
  fields.filter(
    (candidate) =>
      candidate.name !== self.name &&
      CONTROLLER_FIELD_TYPES.has(candidate.fieldType)
  );

export const isBooleanController = (field: IField | undefined) =>
  field?.fieldType === 'Checkbox' ||
  (field?.fieldType === 'OrderComponent' &&
    isBooleanOrderComponent(field.componentCode));

const findField = (fields: IField[], name: string) =>
  fields.find(
    (candidate) => candidate.name.toLowerCase() === name.toLowerCase()
  );

const typedValue = (
  controller: IField | undefined,
  text: string
): VisibilityScalar | undefined => {
  if (isBooleanController(controller)) {
    return text === 'true' ? true : text === 'false' ? false : undefined;
  }
  if (controller?.fieldType === 'Number') {
    const value = Number(text);
    return text !== '' && Number.isFinite(value) ? value : undefined;
  }
  return text;
};

const conditionOf = (
  row: VisibilityRowDraft,
  controller: IField | undefined,
  maxAllowlistSize: number
): VisibilityCondition | string => {
  const texts =
    row.operator === 'in'
      ? [
          ...new Set(
            row.text
              .split(LINE_BREAK)
              .map((line) => line.trim())
              .filter(Boolean)
          ),
        ]
      : [row.text.trim()].filter(Boolean);
  if (texts.length === 0) {
    return `Enter a value for ${controller?.label || row.controller}.`;
  }
  if (row.operator === 'in' && texts.length > maxAllowlistSize) {
    return `Use at most ${maxAllowlistSize} values for ${
      controller?.label || row.controller
    }.`;
  }
  const values = texts.map((text) => typedValue(controller, text));
  if (values.some((value) => value === undefined)) {
    return `Enter valid values for ${controller?.label || row.controller}.`;
  }
  return {
    field: controller?.name ?? row.controller,
    operator: row.operator,
    values: values as VisibilityScalar[],
  };
};

const conditionsOf = (
  rows: VisibilityRowDraft[],
  fields: IField[],
  maxAllowlistSize: number
): VisibilityCondition[] | string => {
  if (rows.length === 0) return 'Add at least one condition.';
  const conditions: VisibilityCondition[] = [];
  for (const row of rows) {
    const controller = findField(fields, row.controller);
    if (!controller) return 'Select a field for every condition.';
    const condition = conditionOf(row, controller, maxAllowlistSize);
    if (typeof condition === 'string') return condition;
    conditions.push(condition);
  }
  const names = conditions.map((condition) => condition.field.toLowerCase());
  if (new Set(names).size !== names.length) {
    return 'Each field can only be used in one condition.';
  }
  return conditions;
};

const rowOf = (condition: VisibilityCondition): VisibilityRowDraft => ({
  controller: condition.field,
  operator: condition.operator,
  text: condition.values.map(String).join('\n'),
});

export const NEW_VISIBILITY_ROW: VisibilityRowDraft = {
  controller: '',
  operator: 'equals',
  text: '',
};

export const draftFromField = (
  field: IField,
  fields: IField[],
  maxAllowlistSize = MAX_VISIBILITY_ALLOWLIST_SIZE
): VisibilityDraft => {
  if (!field.dependees) return { mode: 'always', rows: [], editable: true };
  const mode: VisibilityMode = field.effect === 'Hide' ? 'Hide' : 'Show';
  try {
    const parsed = parseVisibilityConditions(field.dependees, field.label);
    const rows = parsed.map(rowOf);
    const rebuilt = conditionsOf(rows, fields, maxAllowlistSize);
    const editable =
      typeof rebuilt !== 'string' &&
      serializeVisibilityConditions(rebuilt) ===
        serializeVisibilityConditions(parsed);
    return { mode, rows, editable };
  } catch {
    return { mode, rows: [], editable: false };
  }
};

export const ruleFromDraft = (
  field: IField,
  draft: VisibilityDraft,
  fields: IField[],
  maxAllowlistSize = MAX_VISIBILITY_ALLOWLIST_SIZE
): VisibilityRuleResult => {
  if (!draft.editable) {
    return { rule: { dependees: field.dependees, effect: field.effect } };
  }
  if (draft.mode === 'always') {
    return { rule: { dependees: undefined, effect: undefined } };
  }
  const conditions = conditionsOf(draft.rows, fields, maxAllowlistSize);
  if (typeof conditions === 'string') return { error: conditions };

  const rule: VisibilityRule = {
    dependees: serializeVisibilityConditions(conditions),
    effect: draft.mode,
  };
  const candidate = fields.map((item) =>
    item.name === field.name ? { ...item, ...rule } : item
  );
  const cycle = evaluateOrderComponentVisibility({
    fields: candidate,
    answers: {},
    availableTimeServiceCodes: [],
  }).get(field.name.toLowerCase())?.error;
  return cycle ? { error: cycle } : { rule };
};
